import { useCallback } from 'react'

import { useMutationLinkGoogleCalendar, useMutationUnlinkGoogleCalendar } from '@flecto/core/shared'
import { useTranslation } from '@flecto/translations'
import { type CoreUserIdentity } from '@flecto/types'
import { useModal, useNotifications } from '@flecto/ui'
import { useHandleAxiosError } from '@flecto/utils'

import { ModalConfirmationPermission } from '../ModalConfirmationPermission/ModalConfirmationPermission'

export type UseGoogleCalendarActionsOptions = {
  identity: CoreUserIdentity
  isUnifiedEvent?: boolean
}

export function useGoogleCalendarActions({
  identity,
  isUnifiedEvent,
}: UseGoogleCalendarActionsOptions) {
  const { closeModal } = useModal()
  const { showNotification } = useNotifications()
  const { t } = useTranslation('saas', { keyPrefix: 'profile_account' })
  const { handleError } = useHandleAxiosError()

  const {
    mutateAsync: linkGCal,
    isLoading: isLoadingCalendarActivation,
  } = useMutationLinkGoogleCalendar()

  const {
    mutateAsync: unlinkGCal,
    isLoading: isLoadingCalendarDeactivation,
  } = useMutationUnlinkGoogleCalendar()

  const { openModal: openModalConfirmationPermission } = useModal(
    <ModalConfirmationPermission />
  )

  const isLoading = isLoadingCalendarActivation || isLoadingCalendarDeactivation

  const unlinkGoogleCalendar = useCallback(async () => {
    try {
      await unlinkGCal({ auth0_id: identity.auth0_id })

      showNotification({
        type: 'success',
        message: t('notifications.google_calendar_unlinked'),
      })
    } catch {
      showNotification({
        type: 'error',
        message: t('notifications.alert_error_general'),
      })
    }
  }, [identity])

  const linkGoogleCalendar = useCallback(async () => {
    try {
      if (identity?.google_calendar_enabled) {
        await unlinkGCal({ auth0_id: identity.auth0_id })
      }

      await linkGCal({
        auth0_id: identity.auth0_id,
        options: {
          unified_event: isUnifiedEvent,
        },
      })

      showNotification({
        type: 'success',
        message: t('notifications.google_calendar_linked'),
      })

      closeModal()
    } catch (error) {
      handleError(error, {
        'user[base]': openModalConfirmationPermission,
      })

      showNotification({
        type: 'error',
        message: t('notifications.alert_error_general'),
      })
    }
  }, [identity, isUnifiedEvent])

  return { linkGoogleCalendar, unlinkGoogleCalendar, isLoading }
}
